<script setup lang="ts">
import VChart from 'vue-echarts'
import { usePieChartWidget } from '@/composables/widgets/usePieChartWidget'
import { useI18n } from '@/composable/useI18n'
import type { Aggregation, IWidget } from '@/interfaces/components/dashboard/widget'
import type { AggregationKey } from '@/interfaces/api/widget'
import { useCompanyDashboardStore } from '@/store/companyDashboard'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { resolveWidgetDataFields } from '@/utils/sensor-schema-fields'
import { indicatorSourceKeyFromWidget, isGroupWidget } from '@/utils/dashboard/widget-source'

const props = defineProps<{
  widget: IWidget
}>()

const { t } = useI18n()
const store = useCompanyDashboardStore()
const { period, indicatorSources } = storeToRefs(store)
const { getOption } = usePieChartWidget()

const pieChartLoading = computed(
  () => store.widgetData[props.widget.id]?.loading ?? false,
)
const pieChartError = computed(
  () => store.widgetData[props.widget.id]?.error ?? false,
)

const chartOption = computed(() => getOption(props.widget))

const dataFields = computed(() => resolveWidgetDataFields(props.widget.config))

const hasChartData = computed(() => {
  const dataFields = resolveWidgetDataFields(props.widget.config)
  if (dataFields.length === 0) return false

  const aggregation = (
    isGroupWidget(props.widget) ? 'last' : (props.widget.config.aggregation || 'mean')
  ) as Aggregation
  const aggregationKey = aggregation as AggregationKey

  return dataFields.some((dataField) => {
    const key = indicatorSourceKeyFromWidget(props.widget, dataField)
    if (!key) return false
    const source = indicatorSources.value[key]
    const value = source?.aggregations?.[aggregationKey] ?? source?.aggregations?.last
    return value != null
  })
})

</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col">
    <div
      v-if="!period || (pieChartLoading && !hasChartData)"
      class="flex flex-1 items-center justify-center text-sm text-body"
    >
      {{ t('custom_dashboards.view.pie_chart.loading') }}
    </div>

    <div
      v-else-if="pieChartError"
      class="flex flex-1 items-center justify-center text-sm text-red"
    >
      {{ t('custom_dashboards.view.pie_chart.error') }}
    </div>

    <div
      v-else-if="!hasChartData"
      class="flex flex-1 items-center justify-center text-sm text-body"
    >
      {{ t('custom_dashboards.view.pie_chart.empty') }}
    </div>

    <VChart
      v-else
      :option="chartOption"
      class="h-full min-h-0 w-full flex-1"
      autoresize
    />
  </div>
</template>
